import { ConversationRatingEligibilityEvaluationResults } from 'lib-shared-types-api';

import { getGeneralIneligibleText } from './getStatusText';

type EvaluationSummary = {
  text: string;
  secondaryText?: string;
};

/**
 * Generates a summary of the filter evaluation for the collapsed view. Shows
 * high-level outcome: whether rating was offered and why/why not.
 */
export const getEvaluationSummary = (
  evaluationData: ConversationRatingEligibilityEvaluationResults,
): EvaluationSummary => {
  // Check for general ineligible reasons first
  if (
    evaluationData.generalIneligibleReasons &&
    evaluationData.generalIneligibleReasons.length > 0
  ) {
    const reason = getGeneralIneligibleText(
      evaluationData.generalIneligibleReasons[0],
    );

    return {
      text: 'Rating not offered',
      secondaryText: reason,
    };
  }

  // Rating was offered - show success summary
  if (evaluationData.highestPrioEligibleForInfo) {
    const { filter, survey, language, contactPointValueTarget } =
      evaluationData.highestPrioEligibleForInfo;

    return {
      text: `Rating offered - Survey "${survey.name}" was offered to "${contactPointValueTarget}" in language "${language.toUpperCase()}"`,
      secondaryText: `Matched filter: ${filter.name}`,
    };
  }

  // Rating was not offered - determine why
  const rejectionReason = getRejectionReason(evaluationData.filterEvaluations);

  return {
    text: 'Rating not offered',
    secondaryText: rejectionReason,
  };
};

/** Analyzes filter evaluations to determine why rating was not offered. */
const getRejectionReason = (
  filterEvaluations: ConversationRatingEligibilityEvaluationResults['filterEvaluations'],
): string => {
  if (!filterEvaluations || filterEvaluations.length === 0) {
    return 'No filters configured';
  }

  // Check for common failure patterns
  const allDontMatch = filterEvaluations.every(
    (f) => f.kind === 'DontMatchOnConditions',
  );
  if (allDontMatch) {
    return 'No filters matched conversation conditions';
  }

  const hasInvalid = filterEvaluations.some((f) => f.kind === 'Invalid');
  if (hasInvalid) {
    return 'Configuration error in filters';
  }

  const hasNoMatchingAgent = filterEvaluations.some(
    (f) => f.kind === 'NoMatchingAgent',
  );
  if (hasNoMatchingAgent) {
    return 'No matching agent available';
  }

  // Check if filters matched but channels failed
  const hasMatchedFilter = filterEvaluations.some(
    (f) => f.kind === 'MatchOnConditions',
  );
  if (hasMatchedFilter) {
    // Check if the issue is missing contact points
    const hasMissingContact = filterEvaluations.some((f) =>
      f.outgoingChannelEvaluations.some(
        (c) => c.kind === 'EndUserDoesNotHaveContactPointOfKindNeededByChannel',
      ),
    );
    if (hasMissingContact) {
      return 'Customer missing required contact point';
    }

    const hasMissingSurvey = filterEvaluations.some((f) =>
      f.outgoingChannelEvaluations.some((c) => c.kind === 'SurveyDoesNotExist'),
    );
    if (hasMissingSurvey) {
      return 'Survey not found';
    }

    return 'No eligible channel found';
  }

  return 'No eligible filters found';
};
